// A .zip file made in the browser, with no library.
//
// Session 53 follow-up (Leena, 2026-10-14): Mailchimp's "Code your own" offers
// Paste in code, Import ZIP and Import from URL, and the picker under Import
// ZIP refuses a bare .html. So the For Mailchimp download is a zip holding the
// one HTML file. The file is about 10 KB with its styles inline and its images
// hosted, so the entries are stored, not deflated: a zip is a list of local
// headers + data, then a central directory, then an end record (PKWARE
// APPNOTE 4.3.7-4.3.16). Names are written as UTF-8 with the language flag
// (bit 11) set. Dates are the DOS pair; anything before 1980 clamps to 1980.

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xEDB88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

export function crc32(bytes) {
  let c = 0xFFFFFFFF;
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xFF] ^ (c >>> 8);
  return (c ^ 0xFFFFFFFF) >>> 0;
}

function dosDateTime(d) {
  const y = Math.max(d.getFullYear(), 1980);
  const time = (d.getHours() << 11) | (d.getMinutes() << 5) | (d.getSeconds() >> 1);
  const date = ((y - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate();
  return { time, date };
}

/** entries: [{ name, data }] where data is a string (UTF-8) or Uint8Array.
 *  Returns a Uint8Array holding the zip. */
export function zipStored(entries, now = new Date()) {
  const enc = new TextEncoder();
  const { time, date } = dosDateTime(now);
  const parts = [];
  const central = [];
  let offset = 0;

  for (const e of entries) {
    const name = enc.encode(e.name);
    const data = typeof e.data === 'string' ? enc.encode(e.data) : e.data;
    const crc = crc32(data);

    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034b50, true);     // local file header
    local.setUint16(4, 20, true);             // version needed: 2.0
    local.setUint16(6, 0x0800, true);         // flags: UTF-8 names
    local.setUint16(8, 0, true);              // method: stored
    local.setUint16(10, time, true);
    local.setUint16(12, date, true);
    local.setUint32(14, crc, true);
    local.setUint32(18, data.length, true);   // compressed
    local.setUint32(22, data.length, true);   // uncompressed
    local.setUint16(26, name.length, true);
    local.setUint16(28, 0, true);             // extra length
    parts.push(new Uint8Array(local.buffer), name, data);

    const cd = new DataView(new ArrayBuffer(46));
    cd.setUint32(0, 0x02014b50, true);        // central directory header
    cd.setUint16(4, 20, true);                // version made by
    cd.setUint16(6, 20, true);                // version needed
    cd.setUint16(8, 0x0800, true);
    cd.setUint16(10, 0, true);
    cd.setUint16(12, time, true);
    cd.setUint16(14, date, true);
    cd.setUint32(16, crc, true);
    cd.setUint32(20, data.length, true);
    cd.setUint32(24, data.length, true);
    cd.setUint16(28, name.length, true);
    cd.setUint16(30, 0, true);                // extra
    cd.setUint16(32, 0, true);                // comment
    cd.setUint16(34, 0, true);                // disk number start
    cd.setUint16(36, 0, true);                // internal attributes
    cd.setUint32(38, 0, true);                // external attributes
    cd.setUint32(42, offset, true);           // local header offset
    central.push(new Uint8Array(cd.buffer), name);

    offset += 30 + name.length + data.length;
  }

  const cdSize = central.reduce((n, p) => n + p.length, 0);
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true);         // end of central directory
  end.setUint16(4, 0, true);
  end.setUint16(6, 0, true);
  end.setUint16(8, entries.length, true);
  end.setUint16(10, entries.length, true);
  end.setUint32(12, cdSize, true);
  end.setUint32(16, offset, true);
  end.setUint16(20, 0, true);

  const all = [...parts, ...central, new Uint8Array(end.buffer)];
  const out = new Uint8Array(all.reduce((n, p) => n + p.length, 0));
  let pos = 0;
  for (const p of all) { out.set(p, pos); pos += p.length; }
  return out;
}
